import { EJobStatus } from 'business/models/CustomModels';
import { ImageManagementResultDTO } from 'business/models/Models';
import RMTable from 'components/customComponents/Controls/RMTable';
import { useConnectHub, useHubConnection } from 'CustomHooks';
import { useDisconnectHub } from 'CustomHooks/useHubConnection';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import {
  useDisplayResults,
  useImageManagementTasksResultEmpty,
  useImageManagementTasksResults,
  useIsErrorChecked,
  useIsPendingChecked,
  useIsProcessedChecked,
  useIsSelectedTaskError,
  useIsSelectedTaskPreview,
  useIsSelectedTaskRunning,
  useResultTabProgress,
  useSelectedImageTaskId,
  useSetResultCount,
} from 'redux/hooks/imageManagementTask';
import { showAlertSnackBar } from 'utils/Global';
import { JobTypeDTO } from '../constants';
import {
  updateIsImageTaskResultEmpty,
  useOnConnectionStart,
  useUpdateIsSelectedTaskRunning,
  useUpdateSelectedImageTaskResults,
} from '../hooks';
import { actionResultColumns } from '../utils';
import TitleBar from './TitleBar';
import { useRMDispatch } from 'redux/hooks';
import {
  batchUpdateImageTaskResultList,
  setDisplayResults,
  setProgress,
} from 'redux/reducers/imageManagementTaskReducer';

const groupingColumns = ['plateId'];
const tooltipColumns = ['imageStoreBasePath'];

export type ResultHandle = {
  setProgressHandle(val: number): any;
};

const Results = forwardRef<ResultHandle>((props, ref) => {
  const imageManagementTaskResult = useImageManagementTasksResults();
  const isImageManagementTasksResultEmpty = useImageManagementTasksResultEmpty();
  const updateIsImageManagementTaskResultEmpty = updateIsImageTaskResultEmpty();
  const selectedTaskId = useSelectedImageTaskId();
  const isError = useIsErrorChecked();
  const isProcessed = useIsProcessedChecked();
  const isPending = useIsPendingChecked();
  const connection = useHubConnection();
  const onConnectionStart = useOnConnectionStart();
  const onUpdateSelectedImageTaskResults = useUpdateSelectedImageTaskResults();
  const setResultCount = useSetResultCount();
  const setIsSelectedTaskRunning = useUpdateIsSelectedTaskRunning();
  const isSelectedTaskRunning = useIsSelectedTaskRunning();
  const isErrorState = useIsSelectedTaskError();
  const disconnectHub = useDisconnectHub();
  const isPreview = useIsSelectedTaskPreview();
  const displayResults = useDisplayResults();
  const progress = useResultTabProgress();
  const dispatch = useRMDispatch();

  useImperativeHandle(ref, () => ({
    setProgressHandle: (val: number) => {
      dispatch(setProgress(val));
    },
  }));

  useEffect(() => {
    useConnectHub(connection, () =>
      onConnectionStart(connection, selectedTaskId, updateProgress),
    );
    return () => {
      disconnectHub(connection);
    };
  }, [selectedTaskId, isErrorState]);

  useEffect(() => {
    if (imageManagementTaskResult?.length > 0 && displayResults?.length === 0) {
      dispatch(setDisplayResults(imageManagementTaskResult));
      setResultCount(imageManagementTaskResult.length);
    }
  }, [imageManagementTaskResult]);

  useEffect(() => {
    if (
      isPreview == true &&
      progress == 100 &&
      isImageManagementTasksResultEmpty === true &&
      displayResults?.length === 0 &&
      !isErrorState
    ) {
      updateIsImageManagementTaskResultEmpty(false);
      showAlertSnackBar(
        EAlertType.INFO,
        ESnackBarPosition.TOP,
        'No images were found under specified constraints.',
      );
    }
  }, [isImageManagementTasksResultEmpty, isPreview, progress, displayResults]);

  const result = useMemo(() => {
    if (imageManagementTaskResult?.length > 0) {
      let taskResults = [...imageManagementTaskResult];
      if (!isProcessed) {
        taskResults = taskResults.filter((item) => item.actionStatus !== 'Processed');
      }
      if (!isPending) {
        taskResults = taskResults.filter((item) => item.actionStatus !== 'Pending');
      }
      if (!isError) {
        taskResults = taskResults.filter((item) => item.actionStatus !== 'Error');
      }

      setResultCount(taskResults.length);
      return taskResults;
    }
    return [];
  }, [displayResults, isError, isProcessed, isPending, imageManagementTaskResult]);

  const updateProgress = (data) => {
    if (data.jobId != selectedTaskId) {
      return;
    }
    const notFinished =
      data.jobStatus !== EJobStatus.Completed &&
      data.jobStatus !== EJobStatus.Cancelled &&
      data.jobStatus !== EJobStatus.Error;
    setIsSelectedTaskRunning(notFinished);
    getJobProgress(data.jobId, data.progress);
    if (data.jobStatus == EJobStatus.Error) {
      dispatch(batchUpdateImageTaskResultList({ key: 'actionStatus', value: 'Error' }));
      const errorLog =
        data.logs?.[0] ??
        'Inspection count exceeds limit. Reduce the number of inspections by adjusting the filter parameters to continue.';
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, errorLog);
    } else if (data.dataObj !== null) {
      onUpdateSelectedImageTaskResults({
        key: 'actionStatus',
        value: data.dataObj,
        selectedTaskId: selectedTaskId,
      });
    }
  };

  const getJobProgress = (jobId, progressArg = null) => {
    if (jobId != selectedTaskId) {
      return;
    }
    if (progressArg != null && progress !== progressArg) {
      dispatch(setProgress(progressArg));
    } else {
      connection
        .invoke('GetJobsById', jobId.toString(), JobTypeDTO.ImageManangement)
        .then((data) => {
          dispatch(setProgress(data.progress));
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    }
  };

  const getContent = (progress: number, results: ImageManagementResultDTO[]) => {
    if (results.length > 0) {
      return (
        <RMTable
          columns={actionResultColumns}
          data={results}
          mainContainerStyle={{ height: '100%', width: 'stretch', overflow: 'auto' }}
          groupingColumns={groupingColumns}
          hiddenColumns={groupingColumns}
          tooltipColumns={tooltipColumns}
          key="image-management-task-results-table"
        />
      );
    }

    if (results.length === 0 && progress === 100 && !isError) {
      return (
        <div className="flex flex-row h-full w-full items-center justify-center">
          No results to display.
        </div>
      );
    }

    if (progress < 100 && isSelectedTaskRunning) {
      return (
        <div className="flex flex-row h-full w-full items-center justify-center">
          {isPreview ? 'Loading preview...' : 'Running task...'}
        </div>
      );
    }

    return (
      <div className="flex flex-row h-full w-full items-center justify-center">
        No results to display. Please select preview or start a task to view results.
      </div>
    );
  };

  return (
    <div
      id="imagemanagementtask-results-wrapper"
      className="flex flex-column w-full resultHeight"
    >
      <TitleBar
        results={result as any}
        progress={progress}
        setProgress={(val) => dispatch(setProgress(val))}
      />
      {getContent(progress, result as any)}
    </div>
  );
});

type StatusColProps = {
  id: number;
  status: string;
};

export const StatusField = (props: StatusColProps) => {
  const imageManagementTaskResult = useImageManagementTasksResults();
  const isErrorstate: boolean = useIsSelectedTaskError();
  const rowState =
    imageManagementTaskResult?.find((q) => q.imageId == props.id)?.actionStatus ??
    props.status;
  const className =
    (isErrorstate && rowState !== 'Processed') || rowState === 'Error'
      ? 'text-red-500'
      : '';

  return (
    <div id={`imagemanagementtask-results-statusfield-${props.id}`} className={className}>
      {rowState}
    </div>
  );
};

export default Results;
